import { useEffect, useState } from "react";
import { api, formatINR } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, PageBody, Section, StatCell } from "../components/layout/Page";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DownloadSimple } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function Reports() {
  const { user } = useAuth();
  const { activeCompanyId, companies } = useCompany();
  const [scope, setScope] = useState("all");
  const [data, setData] = useState(null);

  const cid = scope === "all" ? null : scope;

  useEffect(() => {
    if (user?.role !== "super_admin" && activeCompanyId) {
      setScope(activeCompanyId);
    }
  }, [user, activeCompanyId]);

  useEffect(() => {
    const load = async () => {
      const params = cid ? { company_id: cid } : {};
      const { data } = await api.get("/reports/dashboard", { params });
      setData(data);
    };
    load();
  }, [cid]);

  const exportEmployees = async () => {
    const res = await api.get("/reports/export-employees", { params: cid ? { company_id: cid } : {}, responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "employees.xlsx"; a.click();
  };

  const exportPayroll = async () => {
    const res = await api.get("/payroll/export-excel", { params: cid ? { company_id: cid } : {}, responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "payroll.xlsx"; a.click();
  };

  if (!data) return <PageBody>Loading...</PageBody>;

  return (
    <div data-testid="reports-page">
      <PageHeader eyebrow="Analytics" title="Reports" subtitle="Cross-business analytics and exports.">
        {user?.role === "super_admin" && (
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-56 rounded-sm" data-testid="reports-scope"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All companies</SelectItem>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button variant="outline" onClick={exportEmployees} className="rounded-sm text-xs uppercase tracking-wider" data-testid="export-emp-btn">
          <DownloadSimple size={14} className="mr-1.5" /> Employees
        </Button>
        <Button variant="outline" onClick={exportPayroll} className="rounded-sm text-xs uppercase tracking-wider" data-testid="export-pay-btn">
          <DownloadSimple size={14} className="mr-1.5" /> Payroll
        </Button>
      </PageHeader>
      <PageBody className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-border border border-border rounded-sm overflow-hidden">
          <StatCell label="Headcount" value={data.total_employees} />
          <StatCell label="Present today" value={data.present_today} accent="text-[#12B886]" />
          <StatCell label="Pending leaves" value={data.pending_leaves} accent="text-[#FAB005]" />
          <StatCell label="Open positions" value={data.open_positions} accent="text-[#2563EB]" />
        </div>

        {data.by_company?.length > 0 && (
          <Section title="Headcount by company">
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={data.by_company}>
                  <CartesianGrid strokeDasharray="2 2" stroke="#E2E8F0" />
                  <XAxis dataKey="company" tick={{ fontSize: 10, fill: "#475569" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#475569" }} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 2, fontSize: 12 }} />
                  <Bar dataKey="count" fill="#2563EB" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Section>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border rounded-sm overflow-hidden">
          <StatCell label="Payslips" value={data.payroll?.count || 0} />
          <StatCell label="Total disbursed" value={formatINR(data.payroll?.total_net || 0)} accent="text-[#12B886]" />
          <StatCell label="Recruitment events" value={Object.values(data.recruitment_by_stage || {}).reduce((a, b) => a + b, 0)} />
        </div>
      </PageBody>
    </div>
  );
}
